import { Injectable, ElementRef, inject } from '@angular/core';
import { WINDOW_TOKEN } from '../../../services/window.token';

@Injectable({ providedIn: 'root' })
export class JoyrideDocumentService {
  private documentHeight: number;
  private readonly browserWindow = inject(WINDOW_TOKEN);

  constructor() {
    this.setDocumentHeight();
    const doc = this.browserWindow.document;
    if (doc && !doc.elementsFromPoint) {
      // IE 11 - Edge browsers
      doc.elementsFromPoint = this.elementsFromPoint.bind(this);
    }
  }

  getElementFixedTop(elementRef: ElementRef): number {
    return elementRef.nativeElement.getBoundingClientRect().top;
  }

  getElementFixedLeft(elementRef: ElementRef): number {
    return elementRef.nativeElement.getBoundingClientRect().left;
  }

  getElementAbsoluteTop(elementRef: ElementRef): number {
    const scrollOffsets = this.getScrollOffsets();
    return (
      elementRef.nativeElement.getBoundingClientRect().top +
      scrollOffsets.y
    );
  }

  getElementAbsoluteLeft(elementRef: ElementRef): number {
    const scrollOffsets = this.getScrollOffsets();
    return (
      elementRef.nativeElement.getBoundingClientRect().left +
      scrollOffsets.x
    );
  }

  setDocumentHeight(): void {
    this.documentHeight = this.calculateDocumentHeight();
  }

  getDocumentHeight(): number {
    return this.documentHeight;
  }

  isParentScrollable(elementRef: ElementRef): boolean {
    return (
      this.getFirstScrollableParent(elementRef.nativeElement) !==
      this.browserWindow.document.body
    );
  }

  isElementBeyondOthers(
    elementRef: ElementRef,
    isElementFixed: boolean,
    keywordToDiscard: string
  ): number {
    const x1 = isElementFixed
      ? this.getElementFixedLeft(elementRef)
      : this.getElementAbsoluteLeft(elementRef);
    const y1 = isElementFixed
      ? this.getElementFixedTop(elementRef)
      : this.getElementAbsoluteTop(elementRef);
    const x2 =
      x1 + elementRef.nativeElement.getBoundingClientRect().width - 1;
    const y2 =
      y1 + elementRef.nativeElement.getBoundingClientRect().height - 1;

    const elements1 = this.browserWindow.document.elementsFromPoint(
      x1,
      y1
    );
    const elements2 = this.browserWindow.document.elementsFromPoint(
      x2,
      y2
    );

    if (elements1.length === 0 && elements2.length === 0) {
      return 1;
    }
    if (
      this.getFirstElementWithoutKeyword(elements1, keywordToDiscard) !==
      elementRef.nativeElement ||
      this.getFirstElementWithoutKeyword(elements2, keywordToDiscard) !==
      elementRef.nativeElement
    ) {
      return 2;
    }
    return 3;
  }

  scrollIntoView(elementRef: ElementRef, isElementFixed: boolean): void {
    const firstScrollableParent = this.getFirstScrollableParent(
      elementRef.nativeElement
    ) as HTMLElement;
    const top = isElementFixed
      ? this.getElementFixedTop(elementRef)
      : this.getElementAbsoluteTop(elementRef);
    if (
      firstScrollableParent !== this.browserWindow.document.body
    ) {
      if (firstScrollableParent.scrollTo) {
        firstScrollableParent.scrollTo(0, top - 150);
      } else {
        // IE 11 - Edge browsers
        firstScrollableParent.scrollTop = top - 150;
      }
    } else {
      this.browserWindow.scrollTo(0, top - 150);
    }
  }

  scrollToTheTop(elementRef: ElementRef): void {
    const firstScrollableParent = this.getFirstScrollableParent(
      elementRef.nativeElement
    ) as HTMLElement;
    if (
      firstScrollableParent !== this.browserWindow.document.body
    ) {
      if (firstScrollableParent.scrollTo) {
        firstScrollableParent.scrollTo(0, 0);
      } else {
        // IE 11 - Edge browsers
        firstScrollableParent.scrollTop = 0;
      }
    } else {
      this.browserWindow.scrollTo(0, 0);
    }
  }

  scrollToTheBottom(elementRef: ElementRef): void {
    const firstScrollableParent = this.getFirstScrollableParent(
      elementRef.nativeElement
    ) as HTMLElement;
    if (
      firstScrollableParent !== this.browserWindow.document.body
    ) {
      if (firstScrollableParent.scrollTo) {
        firstScrollableParent.scrollTo(
          0,
          this.browserWindow.document.body.scrollHeight
        );
      } else {
        // IE 11 - Edge browsers
        firstScrollableParent.scrollTop =
          firstScrollableParent.scrollHeight -
          firstScrollableParent.clientHeight;
      }
    } else {
      this.browserWindow.scrollTo(
        0,
        this.browserWindow.document.body.scrollHeight
      );
    }
  }

  private getFirstScrollableParent(node: HTMLElement): HTMLElement | ParentNode | boolean {
    const regex = /(auto|scroll|overlay)/;

    const style = (node0: HTMLElement, prop: string): string =>
      this.browserWindow
        .getComputedStyle(node0, null)
        .getPropertyValue(prop);

    // TODO I don't know what Navjot was thinking here but those 2 functions below are quite the tangle
    const scroll = (node1: HTMLElement | ParentNode | boolean): boolean =>
      regex.test(
        style(node1 as HTMLElement, 'overflow') +
        style(node1 as HTMLElement, 'overflow-y') +
        style(node1 as HTMLElement, 'overflow-x')
      );

    const scrollparent = (node2: HTMLElement | ParentNode): HTMLElement | ParentNode | boolean => !node2 || node2 === this.browserWindow.document.body
      ? this.browserWindow.document.body
      : scroll(node2)
        ? node2
        : scrollparent(node2.parentNode);

    return scrollparent(node);
  }

  private calculateDocumentHeight(): number {
    const documentRef = this.browserWindow.document;
    return Math.max(
      documentRef.body.scrollHeight,
      documentRef.documentElement.scrollHeight,
      documentRef.body.offsetHeight,
      documentRef.documentElement.offsetHeight,
      documentRef.body.clientHeight,
      documentRef.documentElement.clientHeight
    );
  }

  private getScrollOffsets(): { x: number; y: number} {
    const winReference = this.browserWindow;
    const docReference = this.browserWindow.document;

    // This works for all browsers except IE versions 8 and before
    if (winReference.pageXOffset != null) {
      return { x: winReference.pageXOffset, y: winReference.pageYOffset };
    }

    // For IE (or other browser) in Standards mode // TODO that is probably safe to delete now that we don't care about IE
    if (docReference.compatMode === 'CSS1Compat') {
      return {
        x: docReference.documentElement.scrollLeft,
        y: docReference.documentElement.scrollTop
      };
    }

    // For browsers in Quirks mode
    return {
      x: docReference.body.scrollLeft,
      y: docReference.body.scrollTop
    };
  }

  private elementsFromPoint(x: number, y: number): Array<Element> {
    const parents: Array<Element> = [];
    let parent = void 0;
    do {
      const elem = this.browserWindow.document.elementFromPoint(
        x,
        y
      );
      if (elem && parent !== elem) {
        parent = elem;
        parents.push(parent);
        (parent as HTMLElement).style.pointerEvents = 'none';
      } else {
        parent = false;
      }
    } while (parent);

    parents.forEach(function(parent1: Element) {
      return ((parent1 as HTMLElement).style.pointerEvents = 'all');
    });

    return parents;
  }

  private getFirstElementWithoutKeyword(
    elements: Array<Element>,
    keyword: string
  ): Element {
    while (
      elements[0] &&
      elements[0].classList.toString().includes(keyword)
    ) {
      elements.shift();
    }
    return elements[0];
  }
}
